import {Injectable} from '@angular/core';
import {BehaviorSubject, catchError, combineLatest, Observable, of, tap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {LocalStorageService} from '../../../local-storage.service';
import {SWISS_SERVICES} from '../../../models/local-storage-names';
import {SupplyEntry} from '../../../models/swiss-service.models';
import {ServiceAppModels} from '../../../models/serviceapp-models';
import {HttpClient} from '@angular/common/http';
import {UserAuthenticationService} from '../../../services/user-authentication.service';
import {HelperService} from '../../../services/helper.service';
import {filter} from "rxjs/operators";

interface CurrentOrderDetails {
    isPseudoOrder: boolean;
    identifier: string;
}

@Injectable({
    providedIn: 'root',
})
export class TimeAppSuppliesService {
    /*as boolean cast since
    const a: {a:true} = {a:true}; interface Test  { a:boolean };
    let b: Test = a; //works since b.a can be true
    b.a = false; //shouldn't work since a.a cant be false and is referenced as b.a and a.a is not a boolean but has to be explicitly true*/
    private currentOrderDetails$: BehaviorSubject<CurrentOrderDetails> = new BehaviorSubject({isPseudoOrder: true as boolean, identifier: ''});
    private supplies$: BehaviorSubject<SupplyEntry[]> = new BehaviorSubject([] as SupplyEntry[]);
    private objectSupplies$: BehaviorSubject<SupplyEntry[]> = new BehaviorSubject([] as SupplyEntry[]);

    constructor(private localStorage: LocalStorageService, private http: HttpClient, private userAuthService: UserAuthenticationService, private helperService: HelperService) {
        combineLatest({
            orderDetails: this.currentOrderDetails$,
            supplies: this.supplies$,
            user: userAuthService.getCurrentUser()
        }).pipe(
            takeUntilDestroyed(),
            filter((args) => !!args.user),
            tap(async (args) => {
                let saPosOfOrder: SupplyEntry[] = [];

                const sa_pos: any[] = await this.localStorage.get('supplyEntries', SWISS_SERVICES, false, []);

                if (args.orderDetails.isPseudoOrder) { // If pseudoOrder is active
                    saPosOfOrder = sa_pos.filter(saPos => saPos.objectName == args.orderDetails.identifier); // Filter by pseudoOrderName
                } else { // If pseudoOrder is not active
                    saPosOfOrder = sa_pos.filter(saPos => saPos.objectID == args.orderDetails.identifier); // Filter by currentOrderID
                }
                this.objectSupplies$.next(saPosOfOrder);
            })
        ).subscribe();
    }

    public getObjectSupplies$(): Observable<SupplyEntry[]> {
        return this.objectSupplies$.asObservable();
    }

    public setOrder(orderID: string, pseudoOrderName: string) {
        if (orderID == 'pseudo-order') {
            this.currentOrderDetails$.next({isPseudoOrder: true, identifier: pseudoOrderName});
        } else {
            this.currentOrderDetails$.next({isPseudoOrder: false, identifier: orderID});
        }
    }

    public async deleteEntry(supplyEntryID: string) {
        const supplies = await this.localStorage.get('supplyEntries', SWISS_SERVICES, false, []);
        const i = supplies.findIndex(supplyEntry => supplyEntry.rowID == supplyEntryID);
        if (i > -1) {
            supplies.splice(i);
        } else {
            throw Error('couldnt find given entry in supplies for object');
        }

        await this.localStorage.add('supplyEntries', supplies, SWISS_SERVICES);
        this.supplies$.next(supplies);
    }

    public async addOrUpdateEntry(supplyEntry: SupplyEntry) {
        const supplies = await this.localStorage.get('supplyEntries', SWISS_SERVICES, false, []);
        const i = supplies.findIndex(sE => sE.rowID == supplyEntry.rowID);
        let action = {action: ''};
        if (i > -1) {
            supplies[i] = supplyEntry;
            action = {action: 'Updated'};
        } else {
            supplies.push(supplyEntry);
            action = {action: 'Added'};
        }
        await this.localStorage.add('supplyEntries', supplies, SWISS_SERVICES);

        this.supplies$.next(supplies);
        return of(action);
    }

    public async getSupplyEntry(supplyEntryID: any) {
        const supplies = await this.localStorage.get('supplyEntries', SWISS_SERVICES, false, []);
        return (supplies.find(sE => sE.rowID == supplyEntryID));
    }

    public getCurrentOrderDetails(): CurrentOrderDetails {
        return this.currentOrderDetails$.getValue();
    }

    public getProducts(): Observable<ServiceAppModels.Produkt[]> {
        const url = this.helperService.getSwissWebServiceConfig()!.saUrl;
        return this.http.get<ServiceAppModels.Produkt[]>(url + '/Produkte?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo)).pipe(
            catchError(async err => {
                return await this.localStorage.get('artikel', SWISS_SERVICES);
            })
        ).pipe(
            tap(products => {
                this.localStorage.add('artikel', products, SWISS_SERVICES);
            })
        );
    }

    public async syncSupplyEntries(chosenDay: Date | undefined) {
        let supplyEntriesToSend: SupplyEntry[] = [];
        const supplyEntries: SupplyEntry[] = await this.localStorage.get('supplyEntries', SWISS_SERVICES, false, []);
        if (chosenDay) {
            const chosenDMY = {
                day: chosenDay.getDate(),
                month: chosenDay.getMonth(),
                year: chosenDay.getFullYear()
            };
            supplyEntriesToSend = supplyEntries.filter(entry =>
                entry.readyForExport
                && !entry.exported
                && entry.date.getDate() == chosenDMY.day
                && entry.date.getMonth() == chosenDMY.month
                && entry.date.getFullYear() == chosenDMY.year);
        } else {
            supplyEntriesToSend = supplyEntries.filter(entry => entry.readyForExport && !entry.exported);
        }
        const requests: Promise<{ type: string, rowID: string, error?: string }>[] = [];
        const url = this.helperService.getSwissWebServiceConfig()!.tishUrl + '/UploadSupplyEntry';
        supplyEntriesToSend.forEach((entry) =>
            requests.push(
                new Promise((resolve, reject) => {
                    return this.http.post(url, entry, {
                        headers: {
                            Authorization: `${this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo)}`,
                        },
                    }).subscribe({
                        next: (res: any) => {
                            const i = supplyEntries.findIndex(entry => entry.rowID == res.value);
                            if (i > -1) {
                                supplyEntries[i].exported = true;
                            }
                            resolve({type: 'success', rowID: res.value});
                        },
                        error: (err) => {
                            console.log(err);
                            reject({type: 'error', rowID: err.error.value, error: err.error.error});
                        }
                    });
                })
            ));

        await Promise.allSettled(requests);
        await this.localStorage.add('supplyEntries', supplyEntries, SWISS_SERVICES);
        this.supplies$.next(supplyEntries);
        return;
    }

    public async readyDataForExport(chosenDate: Date) {
        const chosenDMY = {
            day: chosenDate.getDate(),
            month: chosenDate.getMonth(),
            year: chosenDate.getFullYear()
        };
        const supplyEntries: SupplyEntry[] = await this.localStorage.get('supplyEntries', SWISS_SERVICES, false, []);
        supplyEntries.forEach(entry => {
            if (entry.date.getDate() == chosenDMY.day
                && entry.date.getMonth() == chosenDMY.month
                && entry.date.getFullYear() == chosenDMY.year) {
                entry.readyForExport = true;
            }
        });
        await this.localStorage.add('supplyEntries', supplyEntries, SWISS_SERVICES);
        this.supplies$.next(supplyEntries);
    }
}
